<script lang="ts" setup>
import BasicButton from '~/components/form-elements/BasicButton.vue'
import RadioButtons from '~/components/form-elements/RadioButtons.vue'
import CustomModalWindow from '~/components/modal-windows/CustomModalWindow.vue'
import { useSharing } from '~/composables/sharing'
import { useAuthStore } from '~/store/auth'
import type { RadioButtonChoice } from '~/types/form-elements'

const model = defineModel<boolean>()

const props = defineProps<{
  link: string
}>()

type SocialLink = {
  hrefTemplate: string
  icon: string
  name: string
}

const messages = { failedToGetUser: 'Не удалось получить пользователя.' }
const socialLinks: SocialLink[] = [
  { hrefTemplate: 'mailto:?body=$URL_LINK$', icon: 'social-networks/email', name: 'Email' },
  { hrefTemplate: 'https://t.me/share/url?url=$URL_LINK$', icon: 'social-networks/telegram', name: 'Telegram' },
  {
    hrefTemplate: 'https://api.whatsapp.com/send/?text=$URL_LINK$&type=custom_url&app_absent=0',
    icon: 'social-networks/whatsapp',
    name: 'WhatsApp'
  },
  { hrefTemplate: 'https://vk.com/share.php?url=$URL_LINK$', icon: 'social-networks/vk', name: 'ВКонтакте' },
  {
    hrefTemplate: 'https://connect.ok.ru/offer?url=$URL_LINK$',
    icon: 'social-networks/odnoklassniki',
    name: 'Одноклассники'
  }
]

const { $apiHelper, $toast } = useNuxtApp()
const authStore = useAuthStore()
const { copyLink } = useSharing()

const isDataLoading = ref(false)
const isDataLoadingError = ref(false)
const promoCode = ref<string | null>(null)
const promoCodes = ref<any[]>([])

const linkToCopy = computed(() => {
  const promoCodePostfix = promoCode.value ? `?promoCode=${promoCode.value}` : ''

  return `${props.link}${promoCodePostfix}`
})
const promoCodeChoices = computed(
  () => promoCodes.value.map((x: string): RadioButtonChoice => ({ isCode: true, labelText: x, value: x }))
)

async function beforeOpen (): Promise<void> {
  if (authStore.isUser) {
    await loadData()
  }
}

function close (): void {
  model.value = false
}

function getLink (template: string): string {
  return template.replace('$URL_LINK$', encodeURI(linkToCopy.value))
}

async function loadData (): Promise<void> {
  isDataLoading.value = true
  isDataLoadingError.value = false

  try {
    const userResponse = await $apiHelper.users.getUser(authStore.userId as string)
    promoCodes.value = userResponse.data.meta.promoCodes || []

    if (promoCodes.value.length) {
      promoCode.value = promoCodes.value[0]
    }
  } catch (error) {
    console.error(error)

    isDataLoadingError.value = true

    $toast.error(messages.failedToGetUser)
  }

  isDataLoading.value = false
}

function onCopyLinkButtonClick (): void {
  copyLink(linkToCopy.value)

  close()
}
</script>

<template>
  <CustomModalWindow v-model="model" @before-open="beforeOpen">
    <template #title>
      Поделиться ссылкой
    </template>

    <Preloader v-if="isDataLoading" />
    <template v-else>
      <Notification v-if="isDataLoadingError" is-small status="error">
        {{ messages.failedToGetUser }}
      </Notification>

      <div v-if="promoCodes.length" class="mb-6">
        <Notification class="mb-6" is-small status="info">
          Скопируйте ссылку с&nbsp;вашим промокодом или поделитесь&nbsp;ей. Промокод будет добавлен в&nbsp;корзину тому, кто перейдёт по&nbsp;ней.
        </Notification>
        <RadioButtons
          v-model="promoCode"
          :choices="promoCodeChoices"
          is-vertical
          label-text="Промокод"
          name="promoCode"
        />
      </div>

      <div class="flex justify-center space-x-4">
        <div v-for="(x, i) in socialLinks" :key="i" class="cursor-pointer text-center">
          <a class="mb-2 text-[48px]" :href="getLink(x.hrefTemplate)" target="_blank" :title="x.name">
            <SvgIcon :name="x.icon" />
          </a>
        </div>
      </div>
    </template>

    <template v-if="!isDataLoading" #action>
      <BasicButton class="mb-4" color="green" title="Копировать ссылку" @click="onCopyLinkButtonClick" />

      <a v-if="isDataLoadingError" href="#" @click.prevent="loadData">Повторить попытку</a>
      <a v-else href="#" @click.prevent="close">Закрыть</a>
    </template>
  </CustomModalWindow>
</template>
